import { ParsedUrlQuery } from 'querystring';
import { stringify } from 'query-string';
import { SortingMethod } from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import _ from 'lodash';
import { NextRouter } from 'next/router';
import {
  Category,
  Service,
} from '@wix/ambassador-marketplace-categories-v1-category/types';
import {
  IParams,
  SortingMethod as TemplatesSortingMethod,
} from '@common/types';

export const generatePathWithQueryParams = (qs: object) => {
  return !_.isEmpty(qs) ? `?${stringify(qs)}` : '';
};

export const QUERY_PARAMS = {
  lang: 'lang',
  min: 'min',
  max: 'max',
  serviceIds: 'serviceIds',
  sort: 'sort',
  location: 'location',
  currency: 'currency',
  siteType: 'siteType',
  industries: 'industries',
  features: 'features',
  language: 'language',
  languages: 'languages',
  styles: 'styles',
  price: 'price',
  search: 'search',
};

export type QueryParams = {
  lang?: string;
  min?: number;
  max?: number;
  serviceIds?: string;
  sort?: SortingMethod | TemplatesSortingMethod;
  page?: number;
  location?: string;
  currency?: string;

  siteType?: string;
  industries?: string;
  features?: string;
  language?: string;
  languages?: string;
  styles?: string;
  price?: string;
  search?: string;
};

export type ClearAll = {
  services?: boolean;
  location?: boolean;

  siteType?: boolean;
  industries?: boolean;
  features?: boolean;
  price?: boolean;
  language?: boolean;
  languages?: boolean;
  styles?: boolean;
  search?: boolean;

  all?: boolean;
};

export const updateQueryParams = (
  newValues: QueryParams,
  router: NextRouter,
  clearWasClicked?: ClearAll,
  clearAllExcluding?: Omit<ClearAll, 'all'>,
) => {
  const { query } = router;

  if (clearWasClicked?.all) {
    const { currency } = query;
    const clearedParams = { ...newValues };
    if (currency) {
      _.set(clearedParams, 'currency', currency);
    }
    return generatePathWithQueryParams(clearedParams);
  }

  if (!_.isEmpty(clearAllExcluding)) {
    const excludedValues = {};
    const entries = Object.entries(clearAllExcluding);
    entries.map(([key, isExcludedFromClear]) => {
      const value = query[key];
      if (isExcludedFromClear && value) {
        _.set(excludedValues, key, value);
      }
    });
    const updatedParams = { ...excludedValues, ...newValues };
    return generatePathWithQueryParams(updatedParams);
  }

  handlePartialClears({ newValues, clearAllRecord: clearWasClicked });

  const valuesToKeepTheSame = _.difference(
    _.keys(QUERY_PARAMS),
    _.keys(newValues),
  );

  const updatedParams: QueryParams = {};

  _.forEach(valuesToKeepTheSame, (key: string) => {
    const value = query[key];
    if (value) {
      _.set(updatedParams, key, value);
    }
  });

  _.forEach(_.keys(newValues), (key: string) => {
    const value = _.get(newValues, key);
    if (value) {
      _.set(updatedParams, key, value);
    }
  });

  return generatePathWithQueryParams(updatedParams);
};

interface HandleClearAllArgs {
  newValues: QueryParams;
  clearAllRecord?: ClearAll;
}

const handlePartialClears = ({
  newValues,
  clearAllRecord,
}: HandleClearAllArgs) => {
  if (!clearAllRecord) {
    return;
  }

  handleHirePagePartialClears({ newValues, clearAllRecord });

  for (const [filterType, shouldClear] of Object.entries(clearAllRecord)) {
    const newValue = newValues[filterType as keyof typeof newValues];
    if (shouldClear && !newValue) {
      newValues[filterType as keyof typeof newValues] = undefined;
    }
  }
};

const handleHirePagePartialClears = ({
  newValues,
  clearAllRecord,
}: HandleClearAllArgs) => {
  if (clearAllRecord?.services && !newValues.serviceIds) {
    newValues.serviceIds = '';
  }

  if (clearAllRecord?.language && !newValues.lang) {
    newValues.lang = '';
  }

  if (clearAllRecord?.location && !newValues.location) {
    newValues.location = '';
  }
};

export function getServicesFiltersFromUrl(
  query: IParams | ParsedUrlQuery,
  allServicesWithHierarchy: Array<Category>,
  service?: any,
  category?: Category,
) {
  let serviceIds = query.serviceIds as string;
  if (service) {
    serviceIds = service.displayKey!;
  } else if (category) {
    serviceIds = _.map(category.services, (s) => s.displayKey!).join(',');
  }

  let _selectedServices: Array<string> = [];
  if (_.size(serviceIds) > 0) {
    _selectedServices = serviceIds!.split(',');
  }

  const servicesList = _.flatMap(allServicesWithHierarchy, (category) => {
    return category.services!;
  });

  const _services: Array<Service> = _.filter(servicesList, (item) =>
    _selectedServices.includes(item!.displayKey!),
  );

  return _services;
}

export const convertServicesToIds = (_services: Array<Service>) => {
  return _.map(_services, (service) => service.id) as Array<string>;
};
